/**
 * @zendir/ui - Notification Component (Pure React)
 *
 * Toast notification following Astro UX Design System patterns.
 *
 * Features:
 * - Theme-integrated via useTheme()
 * - Status-based styling (standby, normal, caution, critical)
 * - Auto-dismiss support
 * - Close button
 * - Entrance/exit animations
 * - Accessible (live region for screen readers)
 */

import React, { memo, useEffect, useState, useCallback, useRef } from "react";
import { useTheme } from "../theme";
import { classNames } from "../utils";

export type NotificationStatus =
  | "standby"
  | "normal"
  | "caution"
  | "serious"
  | "critical";

export interface NotificationProps {
  /** Notification message */
  message: string;
  /** Status/severity of the notification */
  status?: NotificationStatus;
  /** Whether the notification is open/visible */
  open?: boolean;
  /** Auto-close after this many milliseconds */
  closeAfter?: number;
  /** Show close button */
  closeable?: boolean;
  /** Callback when notification is closed */
  onClose?: () => void;
  /** Custom className */
  className?: string;
  /** Child content (alternative to message prop) */
  children?: React.ReactNode;
}

/**
 * AstroUXDS Status Symbol Icons with Dual-Coding
 *
 * Each status has a unique shape for accessibility:
 * - standby: Ring (info)
 * - normal: Circle (checkmark)
 * - caution: Square (warning)
 * - serious: Diamond (warning)
 * - critical: Triangle (error)
 */
const STATUS_ICONS: Record<NotificationStatus, React.ReactNode> = {
  // Standby - Blue ring with info symbol
  standby: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <circle cx="12" cy="12" r="10" opacity="0.2" />
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M11 11h2v6h-2v-6zm0-4h2v2h-2V7z" />
    </svg>
  ),
  // Normal - Green Circle with checkmark
  normal: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <circle cx="12" cy="12" r="10" />
      <path
        d="M9 12.5l2 2 4-4"
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  // Caution - Yellow Square with warning
  caution: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M11 7h2v6h-2V7zm0 8h2v2h-2v-2z" fill="#000" />
    </svg>
  ),
  // Serious - Orange Diamond with warning
  serious: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <path d="M12 2l10 10-10 10L2 12z" />
      <path d="M11 8h2v6h-2V8zm0 8h2v2h-2v-2z" fill="#000" />
    </svg>
  ),
  // Critical - Red Triangle with exclamation
  critical: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <path d="M12 2L2 20h20L12 2z" />
      <path d="M11 9h2v6h-2V9zm0 8h2v2h-2v-2z" fill="#fff" />
    </svg>
  ),
};

/**
 * Notification - Pure React toast notification
 *
 * Displays alerts with status-based coloring and optional auto-dismiss.
 *
 * @example
 * ```tsx
 * <Notification
 *   message="Telemetry received"
 *   status="normal"
 *   open={true}
 * />
 * <Notification
 *   message="Link quality degraded"
 *   status="caution"
 *   closeAfter={5000}
 *   onClose={() => {}}
 * />
 * ```
 */
export const Notification = memo(function Notification({
  message,
  status = "standby",
  open = true,
  closeAfter,
  closeable = true,
  onClose,
  className = "",
  children,
}: NotificationProps): React.ReactElement | null {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [isVisible, setIsVisible] = useState(open);
  const [isExiting, setIsExiting] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const color = tokens.colors.status[status] || tokens.colors.status.standby;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    closeTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 200);
  }, [onClose]);

  useEffect(() => {
    setIsVisible(open);
    setIsExiting(false);
  }, [open]);

  useEffect(() => {
    if (closeAfter && isVisible) {
      const timer = setTimeout(handleClose, closeAfter);
      return () => clearTimeout(timer);
    }
  }, [closeAfter, isVisible, handleClose]);

  if (!isVisible) return null;

  return (
    <div
      className={classNames("zendir-notification", className)}
      role="alert"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: tokens.spacing.md,
        padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
        backgroundColor: isTransparentTheme
          ? "transparent"
          : tokens.colors.background.surface,
        ...(isTransparentTheme && {
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)" as const,
        }),
        borderLeft: `4px solid ${color}`,
        borderRadius: tokens.borderRadius.md,
        boxShadow: tokens.shadows.lg,
        maxWidth: 400,
        animation: isExiting
          ? "notificationExit 200ms ease-out forwards"
          : "notificationEnter 200ms ease-out",
      }}
    >
      {/* Status icon */}
      <span style={{ color, flexShrink: 0, marginTop: 2 }}>
        {STATUS_ICONS[status]}
      </span>

      {/* Content */}
      <div
        style={{
          flex: 1,
          fontSize: tokens.typography.fontSize.base,
          lineHeight: tokens.typography.lineHeight.normal,
          color: tokens.colors.text.primary,
        }}
      >
        {children ?? message}
      </div>

      {/* Close button */}
      {closeable && (
        <button
          onClick={handleClose}
          aria-label="Close notification"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            padding: 0,
            background: "transparent",
            border: "none",
            borderRadius: tokens.borderRadius.sm,
            color: tokens.colors.text.secondary,
            cursor: "pointer",
            transition: tokens.animation.fast,
            flexShrink: 0,
          }}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      )}

      {/* Animations — respects prefers-reduced-motion (WCAG 2.3.3) */}
      <style>{`
        @keyframes notificationEnter {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes notificationExit {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0;
            transform: translateX(20px);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .zendir-notification {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
});
